import { useState, useEffect, useRef } from 'react'
import { X, ChevronRight, ChevronLeft, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'

interface TourStep {
  id: string
  target: string // CSS selector or data attribute
  title: string
  content: string
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center'
  action?: () => void // Optional action to perform when step is shown
}

export default function DiscoveryTour() {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.language?.toLowerCase().startsWith('ar')
  const [isActive, setIsActive] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [overlayStyle, setOverlayStyle] = useState<React.CSSProperties>({})
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({})
  const [spotlight, setSpotlight] = useState<{ top: number; left: number; width: number; height: number } | null>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  // Tour steps: short, up-to-date, focused on the chat modal
  const steps: TourStep[] = [
    {
      id: 'welcome',
      target: 'body',
      title: t('accessibility.tour.welcomeTitle'),
      content: t('accessibility.tour.welcomeContent'),
      position: 'center',
    },
    {
      id: 'logo',
      target: '[data-tour="site-logo"]',
      title: t('accessibility.tour.logoTitle'),
      content: t('accessibility.tour.logoContent'),
      position: isRTL ? 'left' : 'right',
    },
    {
      id: 'navigation',
      target: '[data-tour="main-nav"]',
      title: t('accessibility.tour.navTitle'),
      content: t('accessibility.tour.navContent'),
      position: 'bottom',
    },
    {
      id: 'chat',
      target: '[data-tour="chat-button"]',
      title: t('accessibility.tour.chatTitle'),
      content: t('accessibility.tour.chatContent'),
      position: 'left',
    },
    {
      id: 'complete',
      target: 'body',
      title: t('accessibility.tour.completeTitle'),
      content: t('accessibility.tour.completeContent'),
      position: 'center',
    },
  ]

  // Check if user has seen the tour
  useEffect(() => {
    const hasSeenTour = localStorage.getItem('sakeena-tour-completed')
    if (!hasSeenTour) {
      // Show tour after a short delay
      setTimeout(() => {
        setIsActive(true)
        setCurrentStep(0)
      }, 1000)
    }
  }, [])

  // Update overlay and tooltip positions
  useEffect(() => {
    if (!isActive) return

    const step = steps[currentStep]
    if (!step) return

    const targetElement = document.querySelector(step.target) as HTMLElement

    if (step.position === 'center' || !targetElement) {
      // Center overlay for welcome/complete steps
      setOverlayStyle({
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        zIndex: 9998,
      })
      setTooltipStyle({
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 9999,
      })
      return
    }

    // Calculate target position (getBoundingClientRect gives viewport coordinates)
    const rect = targetElement.getBoundingClientRect()

    // Create spotlight effect
    const padding = 10
    const spotlight = {
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    }

    // Store spotlight for rendering
    setSpotlight(spotlight)

    // Create overlay
    setOverlayStyle({
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      zIndex: 9998,
    })

    // Position tooltip (using viewport coordinates for fixed positioning)
    // Wait for tooltip to render to get accurate dimensions
    setTimeout(() => {
      const tooltipElement = tooltipRef.current
      if (!tooltipElement) return

      const tooltipPadding = 20
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      const tooltipWidth = tooltipElement.offsetWidth || 320
      const tooltipHeight = tooltipElement.offsetHeight || 200

      let tooltipTop = 0
      let tooltipLeft = 0
      let transform = ''
      let actualPosition = step.position

      // Calculate initial position
      switch (step.position) {
        case 'top':
          tooltipTop = rect.top - tooltipHeight - tooltipPadding
          tooltipLeft = rect.left + rect.width / 2
          transform = 'translateX(-50%)'
          // If tooltip goes above viewport, switch to bottom
          if (tooltipTop < 10) {
            actualPosition = 'bottom'
            tooltipTop = rect.bottom + tooltipPadding
          }
          break
        case 'bottom':
          tooltipTop = rect.bottom + tooltipPadding
          tooltipLeft = rect.left + rect.width / 2
          transform = 'translateX(-50%)'
          // If tooltip goes below viewport, switch to top
          if (tooltipTop + tooltipHeight > viewportHeight - 10) {
            actualPosition = 'top'
            tooltipTop = rect.top - tooltipHeight - tooltipPadding
          }
          break
        case 'left':
          tooltipTop = rect.top + rect.height / 2
          tooltipLeft = rect.left - tooltipWidth - tooltipPadding
          transform = 'translateY(-50%)'
          // If tooltip goes left of viewport, switch to right
          if (tooltipLeft < 10) {
            actualPosition = 'right'
            tooltipLeft = rect.right + tooltipPadding
          }
          break
        case 'right':
          tooltipTop = rect.top + rect.height / 2
          tooltipLeft = rect.right + tooltipPadding
          transform = 'translateY(-50%)'
          // If tooltip goes right of viewport, switch to left
          if (tooltipLeft + tooltipWidth > viewportWidth - 10) {
            actualPosition = 'left'
            tooltipLeft = rect.left - tooltipWidth - tooltipPadding
          }
          break
      }

      // Ensure tooltip stays within viewport bounds
      // Horizontal bounds
      if (actualPosition === 'left' || actualPosition === 'right') {
        tooltipLeft = Math.max(10, Math.min(tooltipLeft, viewportWidth - tooltipWidth - 10))
      } else {
        // For top/bottom, ensure horizontal centering doesn't go out of bounds
        const halfTooltipWidth = tooltipWidth / 2
        if (tooltipLeft - halfTooltipWidth < 10) {
          tooltipLeft = halfTooltipWidth + 10
          transform = 'translateX(-50%)'
        } else if (tooltipLeft + halfTooltipWidth > viewportWidth - 10) {
          tooltipLeft = viewportWidth - halfTooltipWidth - 10
          transform = 'translateX(-50%)'
        }
      }

      // Vertical bounds
      if (actualPosition === 'top' || actualPosition === 'bottom') {
        tooltipTop = Math.max(10, Math.min(tooltipTop, viewportHeight - tooltipHeight - 10))
      } else {
        // For left/right, ensure vertical centering doesn't go out of bounds
        const halfTooltipHeight = tooltipHeight / 2
        if (tooltipTop - halfTooltipHeight < 10) {
          tooltipTop = halfTooltipHeight + 10
          transform = 'translateY(-50%)'
        } else if (tooltipTop + halfTooltipHeight > viewportHeight - 10) {
          tooltipTop = viewportHeight - halfTooltipHeight - 10
          transform = 'translateY(-50%)'
        }
      }

      setTooltipStyle({
        position: 'fixed',
        top: `${tooltipTop}px`,
        left: `${tooltipLeft}px`,
        transform: transform,
        zIndex: 9999,
        maxWidth: `${Math.min(tooltipWidth, viewportWidth - 40)}px`,
        maxHeight: `${Math.min(tooltipHeight, viewportHeight - 40)}px`,
      })
    }, 50)

    // Scroll into view if needed
    targetElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })

    // Execute action if provided
    if (step.action) {
      step.action()
    }
  }, [currentStep, isActive])

  // Update positions on scroll/resize
  useEffect(() => {
    if (!isActive) return

    const updatePositions = () => {
      const step = steps[currentStep]
      if (!step || step.position === 'center') return

      const targetElement = document.querySelector(step.target) as HTMLElement
      if (!targetElement) return

      const rect = targetElement.getBoundingClientRect()
      const padding = 10

      setSpotlight({
        top: rect.top - padding,
        left: rect.left - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
      })

      // Update tooltip position with viewport bounds checking
      const tooltipElement = tooltipRef.current
      if (!tooltipElement) return

      const tooltipPadding = 20
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      const tooltipWidth = tooltipElement.offsetWidth || 320
      const tooltipHeight = tooltipElement.offsetHeight || 200

      let tooltipTop = 0
      let tooltipLeft = 0
      let transform = ''
      let actualPosition = step.position

      // Calculate position with bounds checking
      switch (step.position) {
        case 'top':
          tooltipTop = rect.top - tooltipHeight - tooltipPadding
          tooltipLeft = rect.left + rect.width / 2
          transform = 'translateX(-50%)'
          if (tooltipTop < 10) {
            actualPosition = 'bottom'
            tooltipTop = rect.bottom + tooltipPadding
          }
          break
        case 'bottom':
          tooltipTop = rect.bottom + tooltipPadding
          tooltipLeft = rect.left + rect.width / 2
          transform = 'translateX(-50%)'
          if (tooltipTop + tooltipHeight > viewportHeight - 10) {
            actualPosition = 'top'
            tooltipTop = rect.top - tooltipHeight - tooltipPadding
          }
          break
        case 'left':
          tooltipTop = rect.top + rect.height / 2
          tooltipLeft = rect.left - tooltipWidth - tooltipPadding
          transform = 'translateY(-50%)'
          if (tooltipLeft < 10) {
            actualPosition = 'right'
            tooltipLeft = rect.right + tooltipPadding
          }
          break
        case 'right':
          tooltipTop = rect.top + rect.height / 2
          tooltipLeft = rect.right + tooltipPadding
          transform = 'translateY(-50%)'
          if (tooltipLeft + tooltipWidth > viewportWidth - 10) {
            actualPosition = 'left'
            tooltipLeft = rect.left - tooltipWidth - tooltipPadding
          }
          break
      }

      // Clamp to viewport bounds
      if (actualPosition === 'left' || actualPosition === 'right') {
        tooltipLeft = Math.max(10, Math.min(tooltipLeft, viewportWidth - tooltipWidth - 10))
        const halfTooltipHeight = tooltipHeight / 2
        if (tooltipTop - halfTooltipHeight < 10) {
          tooltipTop = halfTooltipHeight + 10
        } else if (tooltipTop + halfTooltipHeight > viewportHeight - 10) {
          tooltipTop = viewportHeight - halfTooltipHeight - 10
        }
      } else {
        tooltipTop = Math.max(10, Math.min(tooltipTop, viewportHeight - tooltipHeight - 10))
        const halfTooltipWidth = tooltipWidth / 2
        if (tooltipLeft - halfTooltipWidth < 10) {
          tooltipLeft = halfTooltipWidth + 10
        } else if (tooltipLeft + halfTooltipWidth > viewportWidth - 10) {
          tooltipLeft = viewportWidth - halfTooltipWidth - 10
        }
      }

      setTooltipStyle({
        position: 'fixed',
        top: `${tooltipTop}px`,
        left: `${tooltipLeft}px`,
        transform: transform,
        zIndex: 9999,
        maxWidth: `${Math.min(tooltipWidth, viewportWidth - 40)}px`,
        maxHeight: `${Math.min(tooltipHeight, viewportHeight - 40)}px`,
      })
    }

    window.addEventListener('scroll', updatePositions, true)
    window.addEventListener('resize', updatePositions)

    return () => {
      window.removeEventListener('scroll', updatePositions, true)
      window.removeEventListener('resize', updatePositions)
    }
  }, [currentStep, isActive])

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1)
    } else {
      completeTour()
    }
  }

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const skipTour = () => {
    completeTour()
  }

  const completeTour = () => {
    setIsActive(false)
    localStorage.setItem('sakeena-tour-completed', 'true')
  }

  const restartTour = () => {
    localStorage.removeItem('sakeena-tour-completed')
    setIsActive(true)
    setCurrentStep(0)
  }

  if (!isActive) {
    return null
  }

  const step = steps[currentStep]
  if (!step) return null

  return (
    <>
      {/* Overlay */}
      <div style={overlayStyle} onClick={step.position === 'center' ? undefined : skipTour} />
      
      {/* Highlight target element */}
      {spotlight && step.position !== 'center' && (
        <div
          style={{
            position: 'fixed',
            top: `${spotlight.top}px`,
            left: `${spotlight.left}px`,
            width: `${spotlight.width}px`,
            height: `${spotlight.height}px`,
            borderRadius: '8px',
            border: '3px solid #3b82f6',
            boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.3), 0 0 20px rgba(59, 130, 246, 0.5)',
            pointerEvents: 'none',
            zIndex: 9997,
            transition: 'all 0.3s ease',
          }}
        />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        style={tooltipStyle}
        className={cn(
          'bg-white dark:bg-gray-800 rounded-lg shadow-2xl p-6',
          'border-2 border-primary-500 dark:border-primary-400',
          'max-w-sm w-[calc(100vw-40px)] max-h-[calc(100vh-40px)]',
          'overflow-y-auto'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {step.title}
            </h3>
          </div>
          <button
            onClick={skipTour}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors"
            aria-label={isRTL ? 'إغلاق' : 'Close'}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <p className="text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
          {step.content}
        </p>

        {/* Progress */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-2">
            <span>
              {t('accessibility.tour.step')} {currentStep + 1} {t('accessibility.tour.of')} {steps.length}
            </span>
            <span>{Math.round(((currentStep + 1) / steps.length) * 100)}%</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className="bg-primary-600 dark:bg-primary-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {currentStep > 0 && (
              <button
                onClick={prevStep}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-lg',
                  'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
                  'hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors'
                )}
              >
                <ChevronRight className={cn('w-4 h-4', !isRTL && 'rotate-180')} />
                <span>{t('accessibility.tour.previous')}</span>
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={skipTour}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
            >
              {t('accessibility.tour.skip')}
            </button>
            <button
              onClick={nextStep}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-lg',
                'bg-primary-600 dark:bg-primary-500 text-white',
                'hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors'
              )}
            >
              <span>{currentStep === steps.length - 1 ? t('accessibility.tour.finish') : t('accessibility.tour.next')}</span>
              <ChevronLeft className={cn('w-4 h-4', !isRTL && 'rotate-180')} />
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

// Export function to restart tour
export const restartDiscoveryTour = () => {
  localStorage.removeItem('sakeena-tour-completed')
  window.location.reload()
}

